import { useMemo, useState } from 'react'
import { TopBar } from '../components/TopBar'
import { Sheet } from '../components/Sheet'
import { Pill } from '../components/Pill'
import { useApp } from '../store/AppContext'
import type { DocAccess, DocumentItem } from '../types'

const fileIcon: Record<string, string> = { pdf: '📕', docx: '📄', default: '📁' }

const accessMeta: Record<DocAccess, { label: string; icon: string; style: string }> = {
  everyone: { label: 'Everyone on team', icon: '🌐', style: 'bg-emerald-100 text-emerald-700' },
  custom: { label: 'Specific people', icon: '👥', style: 'bg-amber-100 text-amber-700' },
  private: { label: 'Private to uploader', icon: '🔒', style: 'bg-slate-200 text-slate-600' },
}

const participantFolders = ['Reports', 'Therapy Recommendations', 'Plans', 'Meeting Minutes', 'Allied Health Letters', 'Correspondence', 'Support Coordination Summaries', 'Onboarding Resources', 'Emergency & Safety', 'Other Documents']

export function Documents() {
  const { currentUser, documents, allUsers, canSeeDocument, addDocument, updateDocumentAccess } = useApp()
  const isOwnerLike = currentUser.role === 'participant' || currentUser.role === 'rep'
  const visibleDocs = documents.filter(canSeeDocument)
  const [folder, setFolder] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [activeDoc, setActiveDoc] = useState<DocumentItem | null>(null)

  const folders = useMemo(() => {
    const map = new Map<string, number>()
    visibleDocs.forEach((d) => map.set(d.folder, (map.get(d.folder) ?? 0) + 1))
    return Array.from(map.entries())
  }, [visibleDocs])

  const shownDocs = visibleDocs
    .filter((d) => (folder ? d.folder === folder : true))
    .filter((d) => d.name.toLowerCase().includes(search.toLowerCase()))

  return (
    <div>
      <TopBar title="Documents" subtitle={currentUser.role === 'coordinator' ? 'Participant document portal' : 'Shared participant files'} />
      <div className="px-4 pt-3">
        {currentUser.role === 'coordinator' && (
          <div className="mb-3 rounded-2xl border border-brand-100 bg-brand-50 p-3 text-xs leading-5 text-brand-800">
            Upload meeting minutes, allied health letters, correspondence, summaries and other participant documents here. Protected case notes belong in the separate Case Notes area.
          </div>
        )}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search documents..." className="input" />
        {!search && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button onClick={() => setFolder(null)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${folder === null ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'}`}>All</button>
            {folders.map(([name, count]) => (
              <button key={name} onClick={() => setFolder(name)} className={`rounded-full px-3 py-1.5 text-xs font-semibold ${folder === name ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-500'}`}>📁 {name} ({count})</button>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-col gap-2">
          {shownDocs.length === 0 && <div className="rounded-2xl border border-dashed border-slate-300 p-4 text-center text-sm text-slate-400">No documents to show.</div>}
          {shownDocs.map((d) => {
            const meta = accessMeta[d.access]
            const uploader = allUsers.find((u) => u.id === d.uploadedByUserId)
            return (
              <button key={d.id} onClick={() => setActiveDoc(d)} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3.5 text-left shadow-card">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-lg">{fileIcon[d.fileType] ?? fileIcon.default}</span>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-900">{d.name}</p>
                  <p className="text-xs text-slate-400">{d.folder} · {uploader?.name}</p>
                </div>
                <Pill className={meta.style}>{meta.icon}</Pill>
              </button>
            )
          })}
        </div>

        <button onClick={() => setShowUpload(true)} className="mt-4 w-full rounded-2xl bg-brand-600 py-3 text-sm font-bold text-white shadow-card">+ Upload Document</button>
      </div>

      <Sheet open={!!activeDoc} onClose={() => setActiveDoc(null)} title={activeDoc?.name ?? ''}>
        {activeDoc && (
          <DocDetail
            doc={activeDoc}
            canManage={isOwnerLike || activeDoc.uploadedByUserId === currentUser.id}
            onSaveAccess={(access, allowedUserIds) => {
              updateDocumentAccess(activeDoc.id, access, allowedUserIds)
              setActiveDoc({ ...activeDoc, access, allowedUserIds })
            }}
          />
        )}
      </Sheet>

      <Sheet open={showUpload} onClose={() => setShowUpload(false)} title="Upload a document">
        <UploadForm onClose={() => setShowUpload(false)} onSave={(d) => { addDocument(d); setShowUpload(false) }} />
      </Sheet>
    </div>
  )
}

function DocDetail({ doc, canManage, onSaveAccess }: { doc: DocumentItem; canManage: boolean; onSaveAccess: (access: DocAccess, allowedUserIds: string[]) => void }) {
  const { allUsers } = useApp()
  const [access, setAccess] = useState<DocAccess>(doc.access)
  const [allowed, setAllowed] = useState<string[]>(doc.allowedUserIds)
  const teamMembers = allUsers.filter((u) => u.role !== 'participant' && u.role !== 'rep')

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">
        <p>{doc.sizeLabel} · {doc.fileType.toUpperCase()} · in {doc.folder}</p>
        <p className="mt-1">Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}</p>
      </div>
      <div>
        <p className="mb-2 text-xs font-semibold text-slate-600">Who can see this document</p>
        <div className="flex flex-col gap-2">
          {(Object.keys(accessMeta) as DocAccess[]).map((a) => (
            <button key={a} disabled={!canManage} onClick={() => setAccess(a)} className={`flex items-center gap-3 rounded-xl border p-3 text-left disabled:opacity-60 ${access === a ? 'border-brand-500 bg-brand-50' : 'border-slate-200'}`}>
              <span className="text-lg">{accessMeta[a].icon}</span>
              <div className="flex-1"><p className="text-sm font-semibold text-slate-900">{accessMeta[a].label}</p></div>
            </button>
          ))}
        </div>
      </div>
      {access === 'custom' && (
        <div>
          <p className="mb-2 text-xs font-semibold text-slate-600">Select people</p>
          <div className="flex flex-col gap-1.5">
            {teamMembers.map((u) => (
              <label key={u.id} className="flex items-center gap-2 rounded-lg px-1 py-1 text-sm text-slate-700">
                <input type="checkbox" disabled={!canManage} checked={allowed.includes(u.id)} onChange={(e) => setAllowed((prev) => e.target.checked ? [...prev, u.id] : prev.filter((id) => id !== u.id))} />
                {u.name} <span className="text-xs text-slate-400">— {u.title}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {canManage && <button onClick={() => onSaveAccess(access, allowed)} className="rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">Save Access Settings</button>}
    </div>
  )
}

function UploadForm({ onClose, onSave }: { onClose: () => void; onSave: (d: DocumentItem) => void }) {
  const { currentUser, allUsers } = useApp()
  const teamMembers = allUsers.filter((u) => u.id !== currentUser.id && u.role !== 'participant' && u.role !== 'rep')
  const [name, setName] = useState('')
  const [folder, setFolder] = useState(currentUser.role === 'coordinator' ? 'Meeting Minutes' : 'Reports')
  const [access, setAccess] = useState<DocAccess>('custom')
  const [allowed, setAllowed] = useState<string[]>([])

  const submit = () => {
    if (!name.trim()) return
    onSave({
      id: `doc-${Date.now()}`,
      name: name.trim().endsWith('.pdf') || name.includes('.') ? name.trim() : `${name.trim()}.pdf`,
      folder,
      fileType: 'pdf',
      sizeLabel: '—',
      uploadedByUserId: currentUser.id,
      uploadedAt: new Date().toISOString(),
      access,
      allowedUserIds: allowed,
    })
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="rounded-xl bg-slate-50 p-3 text-xs text-slate-500">Prototype upload: the secure file picker/storage will be connected when the backend is added.</p>
      <label className="block text-xs font-semibold text-slate-600"><span className="mb-1 block">Document name</span><input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Care Team Meeting Minutes" className="input" /></label>
      <label className="block text-xs font-semibold text-slate-600"><span className="mb-1 block">Document type / folder</span><select value={folder} onChange={(e) => setFolder(e.target.value)} className="input">{participantFolders.map((f) => <option key={f} value={f}>{f}</option>)}</select></label>
      <label className="block text-xs font-semibold text-slate-600"><span className="mb-1 block">Who can see it</span><select value={access} onChange={(e) => setAccess(e.target.value as DocAccess)} className="input"><option value="custom">Specific people</option><option value="everyone">Everyone on team</option><option value="private">Private to me</option></select></label>
      {access === 'custom' && (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-slate-500">Participant and nominee retain participant-owned oversight. Choose any additional team members:</p>
          {teamMembers.map((u) => <label key={u.id} className="flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" checked={allowed.includes(u.id)} onChange={(e) => setAllowed((prev) => e.target.checked ? [...prev, u.id] : prev.filter((id) => id !== u.id))} />{u.name}</label>)}
        </div>
      )}
      <div className="mt-2 flex gap-2"><button onClick={onClose} className="flex-1 rounded-xl border border-slate-200 py-3 text-sm font-semibold text-slate-600">Cancel</button><button onClick={submit} className="flex-1 rounded-xl bg-brand-600 py-3 text-sm font-bold text-white">Upload</button></div>
    </div>
  )
}

